(() => {
  const SELECTORS = {
    gallery: '[data-product-media-gallery]',
    grid: '[data-product-media-grid]',
    carousel: '[data-product-media-carousel]',
    mediaItem: '[data-product-media-item]',
    zoomTrigger: '[data-pdp-media-zoom-trigger]',
    zoom: '[data-product-media-zoom]',
    zoomThumbSlide: '[data-zoom-media-thumb]',
    zoomMainSlide: '[data-zoom-media-slide]',
  };

  const MOBILE_GALLERY_MEDIA_QUERY = '(max-width: 1023px)';
  const initializedGalleries = new WeakSet();
  let variantListenerBound = false;
  let mobileCarouselResizeListenerBound = false;

  function isMobileGalleryViewport() {
    return window.matchMedia(MOBILE_GALLERY_MEDIA_QUERY).matches;
  }

  function getCarouselStartIndex(visibleMediaIds, variantResource = null) {
    if (!visibleMediaIds.length) return 0;

    const featuredMediaId = variantResource?.featured_media?.id;

    if (!featuredMediaId) {
      return 0;
    }

    const featuredIndex = visibleMediaIds.indexOf(String(featuredMediaId));

    return featuredIndex >= 0 ? featuredIndex : 0;
  }

  function syncMobileCarouselPosition(carousel, startIndex = 0) {
    const splide = carousel?.pdpMediaSplide;

    if (!splide) return;

    const boundedIndex = Math.max(0, Math.min(startIndex, splide.length - 1));

    if (splide.index !== boundedIndex) {
      splide.go(boundedIndex);
    }
  }

  function refreshMobileCarouselLayout(carousel) {
    const splide = carousel?.pdpMediaSplide;

    if (!splide) return;

    const activeIndex = splide.index;

    splide.refresh();

    if (splide.index !== activeIndex) {
      splide.go(activeIndex);
    }
  }

  function bindMobileCarouselImageLoadRefresh(carousel) {
    const images = carousel.querySelectorAll('img');

    if (!images.length) {
      refreshMobileCarouselLayout(carousel);
      return;
    }

    let pendingImages = images.length;

    const handleImageReady = () => {
      pendingImages -= 1;

      if (pendingImages <= 0) {
        refreshMobileCarouselLayout(carousel);
      }
    };

    images.forEach((image) => {
      if (image.complete) {
        handleImageReady();
        return;
      }

      image.addEventListener('load', handleImageReady, { once: true });
      image.addEventListener('error', handleImageReady, { once: true });
    });
  }

  function waitForSplide(callback, attempt = 0) {
    if (window.Splide) {
      callback();
      return;
    }

    if (attempt > 80) return;

    window.setTimeout(() => waitForSplide(callback, attempt + 1), 50);
  }

  function normalizeValue(value = '') {
    return String(value)
      .trim()
      .toLowerCase()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-+/g, '-');
  }

  function splitDatasetValues(value = '') {
    if (!value) return [];
    return value
      .split('||')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  function parseFilterPairs(value = '') {
    if (!value) return [];

    return value
      .split(',')
      .map((pair) => pair.trim())
      .filter(Boolean)
      .map((pair) => {
        const [key, rawValue] = pair.split(':');
        return [normalizeValue(key), normalizeValue(rawValue)];
      })
      .filter(([key, filterValue]) => key && filterValue);
  }

  function getAssociatedZoomRoot(productId) {
    if (!productId) return null;

    const selector = `${SELECTORS.zoom}[data-product-id="${String(productId).replace(/"/g, '\\"')}"]`;
    return document.querySelector(selector);
  }

  function captureTemplates(root, selector) {
    const templates = new Map();

    if (!root) return templates;

    root.querySelectorAll(selector).forEach((element) => {
      const mediaId = element.dataset.mediaId;

      if (!mediaId || templates.has(mediaId)) return;

      templates.set(mediaId, element.cloneNode(true));
    });

    return templates;
  }

  function getSelectedOptionsState(root, variantResource = null) {
    const optionNames = splitDatasetValues(root.dataset.optionNames);
    let optionValues = [];

    if (variantResource) {
      if (Array.isArray(variantResource.options) && variantResource.options.length) {
        optionValues = variantResource.options;
      } else {
        optionValues = [variantResource.option1, variantResource.option2, variantResource.option3].filter(Boolean);
      }
    } else {
      optionValues = splitDatasetValues(root.dataset.selectedOptions);
    }

    const selectedOptionsMap = new Map();

    optionNames.forEach((optionName, index) => {
      const normalizedName = normalizeValue(optionName);
      const normalizedValue = normalizeValue(optionValues[index] || '');

      if (normalizedName && normalizedValue) {
        selectedOptionsMap.set(normalizedName, normalizedValue);
      }
    });

    return {
      optionValues,
      selectedOptionsMap,
    };
  }

  function mediaMatchesSelection(mediaElement, selectedOptionsMap) {
    if (!(mediaElement instanceof HTMLElement)) return false;

    const filterPairs = parseFilterPairs(mediaElement.dataset.mediaFilterPairs);

    if (filterPairs.length) {
      return filterPairs.every(([filterKey, filterValue]) => selectedOptionsMap.get(filterKey) === filterValue);
    }

    if (mediaElement.dataset.mediaKind !== 'video' && mediaElement.dataset.mediaType === 'default' && !mediaElement.dataset.shopTheLookSlot) {
      return false;
    }

    return true;
  }

  function bindZoomTriggers(gallery) {
    gallery.querySelectorAll(SELECTORS.zoomTrigger).forEach((trigger) => {
      if (trigger.dataset.zoomBound === 'true') return;

      trigger.dataset.zoomBound = 'true';

      trigger.addEventListener('mousemove', (event) => {
        trigger.dispatchEvent(
          new CustomEvent('zoom-controller-mousemove', {
            bubbles: true,
            detail: { e: event },
          })
        );
      });

      trigger.addEventListener('mouseover', () => {
        trigger.dispatchEvent(new CustomEvent('zoom-controller-mouseover', { bubbles: true }));
      });

      trigger.addEventListener('mouseleave', () => {
        trigger.dispatchEvent(new CustomEvent('zoom-controller-mouseleave', { bubbles: true }));
      });

      trigger.addEventListener('click', () => {
        const slide = Number(trigger.dataset.zoomIndex || 0);

        trigger.dispatchEvent(
          new CustomEvent('show-zoom', {
            bubbles: true,
            detail: { slide },
          })
        );
      });
    });
  }

  function destroyMobileCarousel(carousel) {
    if (!carousel?.pdpMediaSplide) return;

    carousel.pdpMediaSplide.destroy(true);
    carousel.pdpMediaSplide = null;
  }

  function initMobileCarousel(gallery, startIndex = 0) {
    const state = gallery.__pdpMediaState;
    const carousel = state?.carousel;

    if (!carousel) return;

    carousel.pdpMediaPendingStartIndex = startIndex;

    if (!isMobileGalleryViewport()) {
      destroyMobileCarousel(carousel);
      return;
    }

    waitForSplide(() => {
      if (!carousel.isConnected || !isMobileGalleryViewport()) {
        destroyMobileCarousel(carousel);
        return;
      }

      destroyMobileCarousel(carousel);

      const slides = Array.from(carousel.querySelectorAll('.splide__slide')).filter(
        (slide) => !slide.classList.contains('splide__slide--clone')
      );

      if (!slides.length) return;

      const boundedStartIndex = Math.max(0, Math.min(carousel.pdpMediaPendingStartIndex ?? startIndex, slides.length - 1));

      const splide = new Splide(carousel, {
        perPage: 1,
        perMove: 1,
        pagination: true,
        arrows: false,
        type: 'slide',
        drag: true,

        start: boundedStartIndex,
      });


      splide.on('mounted', () => {
        syncMobileCarouselPosition(carousel, carousel.pdpMediaPendingStartIndex ?? boundedStartIndex);
        bindMobileCarouselImageLoadRefresh(carousel);
      });

      splide.mount();
      carousel.pdpMediaSplide = splide;
    });
  }

  function refreshMobileCarousels() {
    document.querySelectorAll(SELECTORS.gallery).forEach((gallery) => {
      const state = gallery.__pdpMediaState;
      const carousel = state?.carousel;

      if (!carousel) return;

      const startIndex = carousel.pdpMediaPendingStartIndex ?? 0;

      if (!isMobileGalleryViewport()) {
        destroyMobileCarousel(carousel);
        return;
      }

      if (carousel.pdpMediaSplide) {
        refreshMobileCarouselLayout(carousel);
        return;
      }

      initMobileCarousel(gallery, startIndex);
    });
  }

  function bindMobileCarouselResizeListener() {
    if (mobileCarouselResizeListenerBound) return;

    let resizeFrame = 0;

    window.addEventListener('resize', () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(refreshMobileCarousels);
    });

    mobileCarouselResizeListenerBound = true;
  }

  function resetZoomModalState() {
    window.resetZoomModalPanzoom?.();

    if (window.zoomModalPanzoom?.instances instanceof Map) {
      window.zoomModalPanzoom.instances.forEach((instance) => {
        instance?.destroy?.();
      });
      window.zoomModalPanzoom.instances.clear();
      window.zoomModalPanzoom.currentInstance = null;
    }

    if (window.zoomModalSliders?.main) {
      window.zoomModalSliders.main.destroy(true);
    }

    if (window.zoomModalSliders?.thumbs) {
      window.zoomModalSliders.thumbs.destroy(true);
    }

    if (window.zoomModalSliders) {
      window.zoomModalSliders.main = null;
      window.zoomModalSliders.thumbs = null;
      window.zoomModalSliders.isInitialized = false;
    }

  }

  function rebuildMobileSlides(gallery, visibleMediaIds) {
    const state = gallery.__pdpMediaState;
    const list = state?.carousel?.querySelector('.splide__list');

    if (!list) return;

    destroyMobileCarousel(state.carousel);
    list.innerHTML = '';

    visibleMediaIds.forEach((mediaId, visibleIndex) => {
      const template = state.mobileTemplates.get(mediaId);

      if (!template) return;

      const slide = template.cloneNode(true);
      slide.classList.remove('hidden');
      const trigger = slide.querySelector(SELECTORS.zoomTrigger);

      if (trigger) {
        trigger.dataset.zoomIndex = String(visibleIndex);
        delete trigger.dataset.zoomBound;
      }

      list.appendChild(slide);
    });
  }

  function rebuildZoomModal(zoomRoot, zoomThumbTemplates, zoomMainTemplates, visibleMediaIds) {
    if (!zoomRoot) return;

    const thumbList = zoomRoot.querySelector('[rel="product_media_gallery_zoomed_thumbs"] .splide__list');
    const mainList = zoomRoot.querySelector('[rel="product_media_gallery_zoomed"] .splide__list');

    if (!thumbList || !mainList) return;

    resetZoomModalState();

    thumbList.innerHTML = '';
    mainList.innerHTML = '';

    visibleMediaIds.forEach((mediaId, visibleIndex) => {
      const thumbTemplate = zoomThumbTemplates.get(mediaId);
      const mainTemplate = zoomMainTemplates.get(mediaId);

      if (thumbTemplate) {
        const thumbSlide = thumbTemplate.cloneNode(true);
        thumbSlide.classList.remove('hidden');
        const thumbTarget = thumbSlide.querySelector('[data-thumbnail-index]');

        if (thumbTarget) {
          thumbTarget.dataset.thumbnailIndex = String(visibleIndex);
        }

        thumbList.appendChild(thumbSlide);
      }

      if (mainTemplate) {
        const mainSlide = mainTemplate.cloneNode(true);
        mainSlide.classList.remove('hidden');
        mainList.appendChild(mainSlide);
      }
    });
  }

  function renderDesktopItems(state, visibleMediaIds) {
    const visibleSet = new Set(visibleMediaIds);
    let visibleIndex = 0;

    state.desktopItems.forEach((item) => {
      const isVisible = visibleSet.has(item.dataset.mediaId);

      if (!isVisible) item.querySelectorAll('video').forEach((video) => video.pause());
      item.classList.toggle('hidden', !isVisible);
      item.dataset.mediaVisible = String(isVisible);

      const trigger = item.querySelector(SELECTORS.zoomTrigger);

      if (trigger && isVisible) {
        trigger.dataset.zoomIndex = String(visibleIndex);
        delete trigger.dataset.zoomBound;
      }
      if (isVisible) visibleIndex += 1;
    });
  }

  function getHeaderOffset() {
    const headerHeight = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue('--header-height')
      .trim();

    const parsedHeaderHeight = Number.parseFloat(headerHeight);

    if (Number.isFinite(parsedHeaderHeight) && parsedHeaderHeight > 0) {
      return parsedHeaderHeight;
    }

    return window.innerWidth >= 1280 ? 160 : 80;
  }

  function getScrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  function scrollGalleryToTop(gallery, state) {
    const desktopTarget = state.desktopItems.find((item) => item.dataset.mediaVisible === 'true');
    const target = window.matchMedia('(min-width: 1024px)').matches
      ? desktopTarget || gallery
      : state.carousel || gallery;

    if (!target) return;

    requestAnimationFrame(() => {
      const targetTop = target.getBoundingClientRect().top + window.scrollY - getHeaderOffset();

      window.scrollTo({
        top: Math.max(targetTop, 0),
        behavior: getScrollBehavior(),
      });
    });
  }

  function renderGallery(gallery, variantResource = null) {
    const state = gallery.__pdpMediaState;

    if (!state) return;

    const { optionValues, selectedOptionsMap } = getSelectedOptionsState(gallery, variantResource);

    if (optionValues.length) {
      gallery.dataset.selectedOptions = optionValues.join('||');

      if (state.zoomRoot) {
        state.zoomRoot.dataset.selectedOptions = optionValues.join('||');
      }
    }

    const hasTaggedMatchForSelection = state.mediaSequence.some((mediaId) => {
      const mediaElement = state.mediaItemsById.get(mediaId);
      const filterPairs = parseFilterPairs(mediaElement?.dataset.mediaFilterPairs);

      return filterPairs.length && filterPairs.every(([filterKey, filterValue]) => selectedOptionsMap.get(filterKey) === filterValue);
    });

    const visibleMediaIdSet = new Set(
      state.mediaSequence.filter((mediaId) => {
        const mediaElement = state.mediaItemsById.get(mediaId);
        return mediaMatchesSelection(mediaElement, selectedOptionsMap);
      })
    );

    if (!hasTaggedMatchForSelection && state.baseMediaId) {
      visibleMediaIdSet.add(state.baseMediaId);
    }

    let visibleMediaIds = state.mediaSequence.filter((mediaId) => visibleMediaIdSet.has(mediaId));

    if (!visibleMediaIds.length && state.baseMediaId && state.mediaItemsById.has(state.baseMediaId)) {
      visibleMediaIds = [state.baseMediaId];
    } else if (!visibleMediaIds.length) {
      visibleMediaIds = [...state.mediaSequence];
    }

    const carouselStartIndex = getCarouselStartIndex(visibleMediaIds, variantResource);

    renderDesktopItems(state, visibleMediaIds);
    rebuildMobileSlides(gallery, visibleMediaIds);
    rebuildZoomModal(state.zoomRoot, state.zoomThumbTemplates, state.zoomMainTemplates, visibleMediaIds);
    bindZoomTriggers(gallery);
    initMobileCarousel(gallery, carouselStartIndex);

    if (variantResource) {
      scrollGalleryToTop(gallery, state);
    }
  }
  function initGallery(gallery) {
    if (initializedGalleries.has(gallery)) return;

    const grid = gallery.querySelector(SELECTORS.grid);
    const carousel = gallery.querySelector(SELECTORS.carousel);
    const desktopItems = grid ? Array.from(grid.querySelectorAll(SELECTORS.mediaItem)) : [];
    const mediaSequence = desktopItems
      .map((item) => item.dataset.mediaId)
      .filter(Boolean);
    const mediaItemsById = new Map();

    desktopItems.forEach((item) => {
      if (item.dataset.mediaId && !mediaItemsById.has(item.dataset.mediaId)) {
        mediaItemsById.set(item.dataset.mediaId, item);
      }
    });

    const zoomRoot = getAssociatedZoomRoot(gallery.dataset.productId);

    gallery.__pdpMediaState = {
      baseMediaId: gallery.dataset.baseMediaId || '',
      carousel,
      desktopItems,
      mediaItemsById,
      mediaSequence,
      mobileTemplates: captureTemplates(carousel?.querySelector('.splide__list'), '.splide__slide[data-media-id]'),
      zoomRoot,
      zoomThumbTemplates: captureTemplates(
        zoomRoot?.querySelector('[rel="product_media_gallery_zoomed_thumbs"] .splide__list'),
        SELECTORS.zoomThumbSlide
      ),
      zoomMainTemplates: captureTemplates(
        zoomRoot?.querySelector('[rel="product_media_gallery_zoomed"] .splide__list'),
        SELECTORS.zoomMainSlide
      ),
    };

    initializedGalleries.add(gallery);
    renderGallery(gallery);
  }

  function initAllGalleries() {
    document.querySelectorAll(SELECTORS.gallery).forEach(initGallery);
    bindMobileCarouselResizeListener();

    if (!variantListenerBound) {
      document.addEventListener('variant:update', (event) => {
        initAllGalleries();

        const productId = String(event.detail?.data?.productId || '');
        const galleries = productId
          ? Array.from(document.querySelectorAll(`${SELECTORS.gallery}[data-product-id="${productId}"]`))
          : Array.from(document.querySelectorAll(SELECTORS.gallery));

        galleries.forEach((gallery) => {
          renderGallery(gallery, event.detail?.resource || null);
        });
      });

      variantListenerBound = true;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllGalleries);
  } else {
    initAllGalleries();
  }

  document.addEventListener('shopify:section:load', initAllGalleries);
})();

// Custom elements also initialize on variant changes and cloned zoom slides.
class PdpVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    this.events = new AbortController();
    const options = { signal: this.events.signal };
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.visible = false;
    this.zoomBlocked = false;
    this.video.muted = true;
    this.motion.addEventListener('change', () => this.updatePlayback(), options);
    document.addEventListener('visibilitychange', () => this.updatePlayback(), options);
    window.addEventListener('zoom-opened', () => {
      this.zoomBlocked = !this.closest('[data-product-media-zoom]');
      this.updatePlayback();
    }, options);
    window.addEventListener('zoom-closed', () => {
      this.zoomBlocked = !!this.closest('[data-product-media-zoom]');
      this.updatePlayback();
    }, options);
    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.updatePlayback();
    });
    this.observer.observe(this);
  }

  updatePlayback() {
    if (this.visible && !document.hidden && !this.motion.matches && !this.zoomBlocked) {
      // Autoplay can be declined by browser or device preferences.
      this.video.play().catch(() => {});
    } else {
      this.video.pause();
    }
  }

  disconnectedCallback() {
    this.video?.pause();
    this.observer?.disconnect();
    this.events?.abort();
  }
}

if (!customElements.get('pdp-video')) customElements.define('pdp-video', PdpVideo);
